import { FilterList } from '@mui/icons-material';
import {
    Badge,
    Box,
    Button,
    CircularProgress,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { addDays, differenceInCalendarDays, endOfDay, parseISO, startOfToday } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, MessageDescriptor, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { Nullable } from 'vitest';
import { getCalendarActivities } from '../../api/activities-nim';
import { DocumentType } from '../../api/dto';
import {
    FilterOperatorEnum,
    FilterTypeEnum,
    SearchFilter,
    SearchFilterTyped,
    toSpringFilterQuery,
} from '../../components/searching/filters';
import { SearchFieldValues } from '../../components/searching/SearchPage';
import { SimpleFiltersDialog } from '../../components/searching/SimpleFiltersDialog';
import { sanitizeFiltersBySchema } from '../../components/searching/utils';
import { TFormElement } from '../../forms/types';
import { UiSchemaType } from '../../forms/UiSchemaType';
import { useDeviceType } from '../../hooks/useDeviceType';
import { useGridTableLocalStorageState } from '../../hooks/useGridTableLocalStorageState';
import { filterSchemas } from '../activities/ActivitySearchPage';
import { Calendar } from './Calendar';
import { getOrgUnitColor } from './ColorMapping';
import { Toolbar } from './Toolbar';
import { DateRange, GanttDocument, ZoomLevel } from './types';

const GANT_DATA_FORMAT = '%d-%M-%Y';
const DATA_PICKER_FORMAT = 'dd-MMM-yyyy';
const INITIAL_CALENDAR_LENGTH = 9;

const DATE_RANGE_TRANSLATIONS: Record<DateRange, MessageDescriptor['id']> = {
    [DateRange.SEVEN_DAYS]: 'calendar.button.week',
    [DateRange.THIRTY_DAYS]: 'calendar.button.month',
    [DateRange.THREE_HUNDRED_SIXTY_FIVE_DAYS]: 'calendar.button.year',
};

const CalendarCard: React.FC = () => {
    const today = startOfToday();
    const defaultEnd = endOfDay(addDays(today, INITIAL_CALENDAR_LENGTH));
    const location = useLocation();
    const intl = useIntl();
    const [zoom, setZoom] = useState<ZoomLevel>(ZoomLevel.DAYS);
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(defaultEnd);
    const [includeLongLasting, handleIncludeLongLastingChange] = useState(false);
    const [filtering, setFiltering] = useState(false);
    const [filtersVersion, setFiltersVersion] = useState(0);
    const [selectedRange, setSelectedRange] = useState<DateRange | null>(null);
    const filterSchema = filterSchemas(intl);
    const handleClose = useCallback(() => setFiltering(false), [setFiltering]);
    const isMobile: boolean = useDeviceType().isMobile;
    const [localStorageState, setLocalStorageState] = useGridTableLocalStorageState(
        intl.formatMessage({ id: 'activity.contract.title' })
    );
    const defaultFilter: Nullable<SearchFilter>[] = [
        {
            field: 'scheduledEnd',
            operator: FilterOperatorEnum.ON_OR_AFTER,
            value: today,
        },
    ];
    const sanitizeFilters = useCallback(
        (filters: Nullable<SearchFilter>[] | undefined | null) =>
            sanitizeFiltersBySchema(filters as Nullable<SearchFilter[]>, filterSchema),
        [filterSchema]
    );
    const [filters, setFilters] = useState<SearchFilterTyped[]>(() => sanitizeFilters(defaultFilter));
    const actualFilters: SearchFilterTyped[] = useMemo(() => {
        return [
            {
                field: 'scheduledStart',
                type: FilterTypeEnum.DATE,
                operator: FilterOperatorEnum.IS_NOT_EMPTY,
                value: startDate,
            },
            {
                field: 'scheduledEnd',
                type: FilterTypeEnum.DATE,
                operator: FilterOperatorEnum.IS_NOT_EMPTY,
                value: endDate,
            },
            ...filters,
            ...(!includeLongLasting
                ? [
                      {
                          field: 'scheduledStart',
                          type: FilterTypeEnum.DATE,
                          operator: FilterOperatorEnum.ON_OR_AFTER,
                          value: startDate,
                      },
                      {
                          field: 'scheduledEnd',
                          type: FilterTypeEnum.DATE,
                          operator: FilterOperatorEnum.ON_OR_BEFORE,
                          value: endDate,
                      },
                  ]
                : [
                      {
                          field: 'scheduledStart',
                          type: FilterTypeEnum.DATE,
                          operator: FilterOperatorEnum.ON_OR_BEFORE,
                          value: endDate,
                      },
                      {
                          field: 'scheduledEnd',
                          type: FilterTypeEnum.DATE,
                          operator: FilterOperatorEnum.ON_OR_AFTER,
                          value: startDate,
                      },
                  ]),
        ];
    }, [filters, includeLongLasting, startDate, endDate]);
    const [activeFilters, setActiveFilters] = useState(0);
    const initialFilters = [] as SearchFilter[];

    const filterForm = useForm<SearchFieldValues>({
        mode: 'onChange',
        defaultValues: { filters: location.state?.filters ? location.state.filters : initialFilters },
    });

    const { data, isFetching, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
        queryKey: ['activities', JSON.stringify(actualFilters)],
        queryFn: async ({ pageParam = 0 }) => {
            const res = await getCalendarActivities({
                filter: toSpringFilterQuery(actualFilters),
                page: pageParam,
                size: 100,
                sort: ['scheduledStart,asc'],
            });
            const rawData = res?.content ?? [];
            const activities = rawData.map<GanttDocument>((activity) => {
                const fullName = activity.activityResponsible?.fullName ?? '';
                const responsible = fullName.includes('(')
                    ? fullName.slice(0, fullName.indexOf('(')).trim()
                    : fullName || 'responsible';
                const orgUnitCode = activity.activityResponsible?.orgUnitCode ?? '';
                const section = orgUnitCode.includes('-')
                    ? orgUnitCode.slice(0, orgUnitCode.lastIndexOf('-'))
                    : 'DEFAULT';
                const dbStartDate = parseISO(activity.scheduledStart!);
                const dbEndDate = endOfDay(parseISO(activity.scheduledEnd!));
                const duration = differenceInCalendarDays(dbEndDate, dbStartDate) + 1;

                return {
                    id: `${DocumentType.ACTIVITY}-${activity.id}`,
                    text: activity.title ?? 'Activity',
                    description: activity.description,
                    start_date: dbStartDate,
                    end_date: dbEndDate,
                    duration,
                    responsible,
                    section,
                    color: getOrgUnitColor(section),
                    type: DocumentType.ACTIVITY,
                    notesDeCoupure: activity.notesDeCoupure,
                    is37s: activity.is37s,
                    vics: activity.vics,
                    lockouts: activity.lockouts,
                    firePermits: activity.firePermits,
                    rpAssessment: activity.rpAssessment,
                };
            });
            return {
                activities,
                totalPages: res.totalPages,
                page: res.pageable.pageNumber,
            };
        },
        getNextPageParam: (lastPage) => {
            return lastPage.page + 1 < lastPage.totalPages ? lastPage.page + 1 : undefined;
        },
    });

    const { currentPage, totalPages } = useMemo(() => {
        if (data && data.pages.length > 0) {
            const page = data.pages[data.pages.length - 1];
            return { currentPage: page.page + 1, totalPages: page.totalPages };
        }
        return data ? { currentPage: null, totalPages: null } : { currentPage: 0, totalPages: 0 };
    }, [data]);

    const activities = useMemo(
        () =>
            data
                ? { data: data.pages.flatMap((page) => page.activities), links: [], lastUpdated: new Date() }
                : { data: [], links: [], lastUpdated: new Date() },
        [data]
    );

    const queryClient = useQueryClient();
    const handleSearch = useMemo(
        () =>
            filterForm.handleSubmit((searchData) => {
                setFilters([]);

                let filteredSearchData = sanitizeFilters(
                    searchData?.filters?.filter((f) => f?.field != null) as SearchFilter[]
                );
                setActiveFilters(filteredSearchData.length);

                if (selectedRange !== null) {
                    const filterEndDate = endOfDay(addDays(today, selectedRange - 1));
                    setStartDate(today);
                    setEndDate(filterEndDate);
                }

                filteredSearchData.push(...sanitizeFilters(defaultFilter));

                setFilters(filteredSearchData);
                queryClient.removeQueries(['activities']);
                filterForm.reset(searchData);
            }),
        [filterForm.handleSubmit, queryClient, sanitizeFilters, selectedRange, startDate, endDate, includeLongLasting]
    );

    const handleSavedSearchStore = useCallback(
        (savedSearchId: number | undefined) => {
            setLocalStorageState({
                ...localStorageState,
                savedFilterId: savedSearchId,
            });
        },
        [setLocalStorageState, localStorageState]
    );

    const handleResetFilters = useCallback(() => {
        handleSavedSearchStore(undefined);
        filterForm.reset({ filters: [] });
        setFiltersVersion((prev) => prev + 1);
        queryClient.removeQueries(['activities']);
        void handleSearch();
    }, [defaultFilter, filterForm.reset, sanitizeFilters, queryClient]);

    const handleStartDateChange = useCallback((newStartDate: Date) => {
        setStartDate(newStartDate);
        setSelectedRange(null);
    }, []);

    const handleEndDateChange = useCallback((newEndDate: Date) => {
        setEndDate(newEndDate);
        setSelectedRange(null);
    }, []);

    const filterFormSchema: TFormElement = useMemo(
        () => ({
            type: UiSchemaType.LAYOUT_VERTICAL,
            elements: [
                {
                    type: UiSchemaType.FIELD_ARRAY,
                    path: 'filters',
                    appendLabel: intl.formatMessage({ id: 'search.filters.add' }),
                    initialValue: {
                        field: null,
                        operator: null,
                        value: null,
                    },
                    element: {
                        type: UiSchemaType.FILTER_GROUP,
                        elements: filterSchema,
                    },
                },
            ],
        }),
        [intl, filterSchema]
    );

    useEffect(() => {
        void handleSearch();
    }, [selectedRange]);

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box
                sx={{
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        gap: isMobile ? 1 : 2,
                        alignItems: 'center',
                        mx: 2,
                        mt: isMobile ? 2 : 0,
                        height: isMobile ? 168.5 : 68.5,
                        flexDirection: isMobile ? 'column' : 'row',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            gap: isMobile ? 1 : 2,
                            alignItems: 'center',
                        }}
                    >
                        <SimpleFiltersDialog
                            title={intl.formatMessage({ id: 'search.filters' })}
                            open={filtering}
                            form={filterForm}
                            formGeneratorKey={filtersVersion}
                            formSchema={filterFormSchema}
                            onClose={handleClose}
                            onCancel={handleClose}
                            onUpdateFilters={() => handleSearch()}
                            onResetFilters={handleResetFilters}
                        />

                        <ToggleButton
                            color="primary"
                            size="small"
                            selected={activeFilters > 0}
                            value="filters"
                            onClick={() => setFiltering((prev) => !prev)}
                        >
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Badge color="primary" badgeContent={activeFilters}>
                                    <FilterList fontSize="small" />
                                </Badge>
                                <Box>
                                    <FormattedMessage id="search.filters" />
                                </Box>
                            </Stack>
                        </ToggleButton>

                        <ToggleButtonGroup
                            color="primary"
                            exclusive
                            value={selectedRange}
                            onChange={(_, newValue) =>
                                setSelectedRange((prev) => (prev === newValue ? null : newValue))
                            }
                            size="small"
                        >
                            {Object.values(DateRange)
                                .filter((v): v is DateRange => typeof v !== 'string')
                                .map((value) => (
                                    <ToggleButton key={value} value={value}>
                                        <FormattedMessage id={DATE_RANGE_TRANSLATIONS[value]} />
                                    </ToggleButton>
                                ))}
                        </ToggleButtonGroup>
                    </Box>
                    <Toolbar
                        zoom={zoom}
                        startDate={startDate}
                        endDate={endDate}
                        includeLongLasting={includeLongLasting}
                        onZoomChange={setZoom}
                        onStartChange={handleStartDateChange}
                        onEndChange={handleEndDateChange}
                        onIncludeLongLastingChange={handleIncludeLongLastingChange}
                        dateFormat={DATA_PICKER_FORMAT}
                    />
                </Box>

                {isFetching && !isFetchingNextPage ? (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            overflow: 'auto',
                            flex: 1,
                            flexDirection: 'column',
                        }}
                    >
                        <Calendar
                            activities={activities}
                            zoom={zoom}
                            startDate={startDate}
                            endDate={endDate}
                            dateFormat={GANT_DATA_FORMAT}
                        />
                        {totalPages && totalPages !== currentPage ? (
                            <Button
                                fullWidth
                                disabled={!hasNextPage || isFetchingNextPage}
                                onClick={() => fetchNextPage()}
                                sx={{
                                    my: 2,
                                    textTransform: 'none',
                                    borderRadius: 0,
                                    height: 48,
                                    justifyContent: 'center',
                                    backgroundColor: '#f5f5f5',
                                    '&:hover': {
                                        backgroundColor: '#e0e0e0',
                                    },
                                }}
                            >
                                <Typography variant="button" color="primary">
                                    {isFetchingNextPage
                                        ? intl.formatMessage({ id: 'calendar.loading' })
                                        : intl.formatMessage(
                                              { id: 'calendar.loadMore' },
                                              {
                                                  current: currentPage,
                                                  total: totalPages ?? '1',
                                              }
                                          )}
                                </Typography>
                            </Button>
                        ) : (
                            <></>
                        )}
                    </Box>
                )}
            </Box>
        </LocalizationProvider>
    );
};

export { CalendarCard };
